import { SecurityCodeMethod } from "../../mfa/hooks/useMfaApi";
import translate, { TranslationKey } from "./translate";

export type InvalidUsernameOrPasswordError = 'isAlreadyUsed' | 'pleaseEnterUsername' | 'passwordGuidelines'
export type GetResetPasswordErrors = 'couldNotProcess' | 'noLongerActive' | 'lockedToProtect' | 'mustLogIn' | 'callToGetTempPassword' | 'invalidMBI' | 'invalidLastName' | 'invalidDOB' | 'invalidUsername' | 'didNotMatch' | 'tempUsername';
export type GetUnlockAccountErrors = InvalidUsernameOrPasswordError | 'notLocked' | 'invalidMBI'

export interface ApiError {
    name: string
    field?: string
    message: string
    type: string
}

export const hasInvalidField = (errors?: ApiError[], fieldName?: string) => {
    return errors?.some(error => error.name === 'InvalidField' && error.field === fieldName);
};

export const get400InvalidUsernameOrPasswordError = (errors: ApiError[]): InvalidUsernameOrPasswordError | null => {
    const isAlreadyUsed = errors?.some(error => error.name === 'AlreadyUsed');
    const invalidUsername = hasInvalidField(errors, 'username') || hasInvalidField(errors, 'updates.username');
    const invalidPassword = hasInvalidField(errors, 'password') || hasInvalidField(errors, 'updates.password');
    if (isAlreadyUsed) {
        return 'isAlreadyUsed';
    }
    if (invalidUsername) {
        return 'pleaseEnterUsername';
    }
    if (invalidPassword) {
        return 'passwordGuidelines';
    }

    return null;
};

export const hasErrorMessage = (errors: ApiError[], message: string) => {
    return errors.some(e => e.message && e.message.includes(message));
}

function getErrorMessageAndAnalytics(errorCode: number, errors: ApiError[]) {
    const analyticsErrorMessages = {
        noLongerActive: "Your account is no longer active. To reactivate your account, call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048.",
        lockedToProtect: "Your account is now locked to protect your information. Unlock My Account.",
        callToGetTempPassword: "We can't find you in our records. Check the information you entered and try again. For more help, call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048.",
        mustLogIn: "You must log in with your temporary password before you can reset your password. If you're unable to log in with your temporary password, call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048.",
        tempUsername: "You must log in with your temporary username before you can reset your password. If you're unable to log in with your temporary username, call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048.",
        isAlreadyUsed: "Enter a new password that's different from your last 6 passwords.",
        pleaseEnterUsername: "Please enter a username that meets the username guidelines.",
        invalidPassword: "The Password does not meet the required guidelines. Please enter the correct information and try again.",
        passwordGuidelines: "The Password does not meet the required guidelines. Please enter the correct information and try again.",
        didNotMatch: "We can't find you in our records. Check the information you entered and try again. For more help, call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048.",
        couldNotProcess: "We can't process your request at this time. We're sorry for any inconvenience. Try resetting your password later.",
        invalidMBI: "Complete these items to reset your password:Medicare Number: Enter your Medicare Number.",
        invalidLastName: "Complete these items to reset your password:Last name: Enter your last name.",
        invalidDOB: "Complete these items to reset your password:Date of birth: Enter your date of birth.",
        invalidUsername: "Complete these items to reset your password:Username: Create a username for your account using the guidelines listed.",
    }

    let errorMessageKey: GetResetPasswordErrors | InvalidUsernameOrPasswordError | null;
    let errorMessageKeyInAnalytics: string | null = null;
    switch (errorCode) {
        case 400:
            const isLocked = errors.some(e => e.name === 'BadRequest' && e.message.includes('locked'));
            const isDisabled = errors.some(e => e.name === 'BadRequest' && e.message.includes('disabled'));
            const isMissingQuestions = hasErrorMessage(errors, 'missing questions');
            const isTempPassword = hasErrorMessage(errors, 'temporary password');
            const isTempUsername = hasErrorMessage(errors, 'temporary username');
            const didNotMatch = hasErrorMessage(errors, 'did not match');
            const invalidMBI = hasInvalidField(errors, 'mbi');
            const invalidUsername = hasInvalidField(errors, 'username');
            const invalidLastName = hasInvalidField(errors, 'lastName');
            const invalidDOB = hasInvalidField(errors, 'dateOfBirth');
            const invalidPasswordErrorMessage = get400InvalidUsernameOrPasswordError(errors);
            if (isDisabled) {
                errorMessageKey = 'noLongerActive';
                errorMessageKeyInAnalytics = analyticsErrorMessages['noLongerActive'];
            } else if (isLocked) {
                errorMessageKey = 'lockedToProtect';
                errorMessageKeyInAnalytics = analyticsErrorMessages['lockedToProtect'];
            } else if (didNotMatch) {
                errorMessageKey = 'didNotMatch';
                errorMessageKeyInAnalytics = analyticsErrorMessages['callToGetTempPassword'];
            } else if (isTempUsername) {
                errorMessageKey = 'tempUsername';
                errorMessageKeyInAnalytics = analyticsErrorMessages['tempUsername'];
            } else if (isMissingQuestions) {
                if (isTempPassword) {
                    errorMessageKey = 'mustLogIn';
                    errorMessageKeyInAnalytics = analyticsErrorMessages['mustLogIn'];
                } else {
                    errorMessageKey = 'callToGetTempPassword';
                    errorMessageKeyInAnalytics = analyticsErrorMessages['callToGetTempPassword'];
                }
            } else if (isTempPassword && !isMissingQuestions) {
                errorMessageKey = 'mustLogIn';
                errorMessageKeyInAnalytics = analyticsErrorMessages['mustLogIn'];
            } else if (invalidPasswordErrorMessage) {
                errorMessageKey = invalidPasswordErrorMessage;
                errorMessageKeyInAnalytics = analyticsErrorMessages[invalidPasswordErrorMessage];
            } else if (invalidMBI) {
                errorMessageKey = 'invalidMBI';
                errorMessageKeyInAnalytics = analyticsErrorMessages['invalidMBI'];
            } else if (invalidUsername) {
                errorMessageKey = 'invalidUsername';
                errorMessageKeyInAnalytics = analyticsErrorMessages['invalidUsername'];
            } else if (invalidLastName) {
                errorMessageKey = 'invalidLastName';
                errorMessageKeyInAnalytics = analyticsErrorMessages['invalidLastName'];
            } else if (invalidDOB) {
                errorMessageKey = 'invalidDOB';
                errorMessageKeyInAnalytics = analyticsErrorMessages['invalidDOB'];
            } else {
                errorMessageKey = 'didNotMatch';
                errorMessageKeyInAnalytics = analyticsErrorMessages['didNotMatch'];
            }
            break;
        case 403:
            if (errors.some(e => e.message.includes('temporary password'))) {
                errorMessageKey = 'mustLogIn';
                errorMessageKeyInAnalytics = analyticsErrorMessages['mustLogIn'];
            } else {
                errorMessageKey = 'couldNotProcess';
                errorMessageKeyInAnalytics = analyticsErrorMessages['couldNotProcess'];
            }
            break;
        case 404:
            errorMessageKey = 'didNotMatch';
            errorMessageKeyInAnalytics = analyticsErrorMessages['didNotMatch'];
            break;
        default:
            errorMessageKey = 'couldNotProcess';
            errorMessageKeyInAnalytics = analyticsErrorMessages['couldNotProcess'];
    }
    return { errorMessageKey, errorMessageKeyInAnalytics };
}

export const getResetPasswordError = (errorCode: number, errors: ApiError[]) => {
    const { errorMessageKey, errorMessageKeyInAnalytics } = getErrorMessageAndAnalytics(errorCode, errors);
    return {
        applicationErrorMessage: errorMessageKey,
        analyticsErrorMessage: errorMessageKeyInAnalytics ? errorMessageKeyInAnalytics : errorMessageKey
    };
};

export const getUnlockAccountError = (errorCode: number, errors: ApiError[]) => {
    let errorMessageKey: GetUnlockAccountErrors | null = null;
    const invalidMBI = errors?.some(error => error.name === 'InvalidField' && error.field === 'mbi');

    if (invalidMBI) {
        return 'invalidMBI';
    }

    if (errorCode === 400) {
        errorMessageKey = get400InvalidUsernameOrPasswordError(errors);
    }

    return errorMessageKey;
};

export const isAlreadyUnlockedMessage = (message: GetUnlockAccountErrors | null) => message === 'notLocked' ? true : false;

export const getMfaDuplicateChannelErrorKey = (errors: ApiError[], type: SecurityCodeMethod): TranslationKey | null => {
    return hasErrorMessage(errors, 'MFA_DUPLICATE_CHANNEL') ? `already_using_${type}` : null;
}
/** Return inline error text if MFA_DUPLICATE_CHANNEL error is present. Returns empty string otherwise. */
export const getMfaDuplicateChannelError = (errors: ApiError[], type: SecurityCodeMethod) => {
    const errKey = getMfaDuplicateChannelErrorKey(errors, type);
    return errKey ? translate(errKey) : '';
}
